import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CloudArrowUp, CircleNotch, CheckCircle, WarningCircle, FileXls, FilePdf, FileCsv, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

const ACCEPT = ".pdf,.xlsx,.xls,.csv";

const MAX_WAIT_MS = 10 * 60 * 1000;

export function UploadPanel({ projectId, hasItems, uploads = [], onDeleteUpload, beforeUpload, onDone, onBusy }) {
  const [mode, setMode] = useState("append");
  const [job, setJob] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const poll = useRef(null);

  useEffect(() => () => { clearInterval(poll.current); onBusy(false); }, [onBusy]);

  const start = async (file) => {
    if (!file) return;
    if (!/\.(pdf|xlsx|xls|csv)$/i.test(file.name)) return toast.error("Upload a PDF, Excel or CSV file");
    if (mode === "replace" && hasItems && !window.confirm("Replace all existing BOQ items with this file?")) return;
    await beforeUpload();
    onBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    setJob({ status: "uploading", filename: file.name });
    try {
      const { data } = await api.post(`/projects/${projectId}/upload`, fd);
      const started = Date.now();
      setJob({ status: "processing", filename: file.name });
      poll.current = setInterval(async () => {
        if (Date.now() - started > MAX_WAIT_MS) {
          clearInterval(poll.current);
          onBusy(false);
          setJob({ status: "failed", filename: file.name, error: "Extraction is taking too long. Please try again." });
          return;
        }
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          if (j.status === "processing") return;
          clearInterval(poll.current);
          onBusy(false);
          setJob({ ...j });
          if (j.status === "done") {
            toast.success(`${j.item_count} BOQ items extracted`);
            onDone();
          } else toast.error(j.error || "Extraction failed");
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      onBusy(false);
      setJob({ status: "failed", filename: file.name, error: errMsg(e, "Upload failed") });
    }
  };

  const busy = job && ["uploading", "processing"].includes(job.status);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
      <div>
        <div
          data-testid="boq-upload-dropzone"
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); !busy && start(e.dataTransfer.files[0]); }}
          className={`border-2 border-dashed rounded-sm p-12 text-center bg-white transition-colors cursor-pointer ${drag ? "border-emerald-500 bg-emerald-50/40" : "border-slate-300 hover:bg-slate-50 hover:border-emerald-500"} ${busy ? "pointer-events-none opacity-90" : ""}`}
        >
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="boq-file-input" onChange={(e) => { start(e.target.files[0]); e.target.value = ""; }} />
          {busy ? (
            <div data-testid="upload-processing">
              <CircleNotch className="animate-spin text-emerald-600 w-12 h-12 mx-auto mb-4" />
              <p className="text-slate-800 font-medium">{job.status === "uploading" ? "Uploading" : "AI is reading your BOQ"} — {job.filename}</p>
              <p className="text-slate-500 text-sm mt-1">Identifying items, quantities, units, rates and categories. Large tenders can take a minute or two.</p>
            </div>
          ) : (
            <>
              <CloudArrowUp className="text-slate-400 w-12 h-12 mx-auto mb-4" weight="duotone" />
              <p className="text-slate-700 font-medium">Drop the Government BOQ here, or click to browse</p>
              <p className="text-slate-400 text-sm mt-1">PDF, Excel (.xlsx / .xls) or CSV · up to 25 MB</p>
              <div className="flex justify-center gap-4 mt-5 text-slate-400">
                <FilePdf size={26} weight="duotone" /><FileXls size={26} weight="duotone" /><FileCsv size={26} weight="duotone" />
              </div>
            </>
          )}
        </div>

        {job?.status === "done" && (
          <div data-testid="upload-success" className="mt-4 flex items-center gap-2 p-3 text-sm border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-sm">
            <CheckCircle size={18} weight="fill" /> {job.item_count} items extracted from {job.filename}. Review them in BOQ Items.
          </div>
        )}
        {job?.status === "failed" && (
          <div data-testid="upload-error" className="mt-4 flex items-center gap-2 p-3 text-sm border border-rose-200 bg-rose-50 text-rose-700 rounded-sm">
            <WarningCircle size={18} weight="fill" /> {job.error}
          </div>
        )}

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="uploaded-files-list">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="font-heading text-lg font-bold text-slate-900">Uploaded BOQ files</h3>
            <p className="text-xs text-slate-500">Deleting a file removes every item that came from it. Items you added by hand stay.</p>
          </div>
          {uploads.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500" data-testid="uploaded-files-empty">No files uploaded yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {uploads.map((u) => (
                <li key={u.source_id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`uploaded-file-${u.source_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{u.filename}</div>
                    <div className="text-xs text-slate-500">{u.item_count} items · {new Date(u.uploaded_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                  </div>
                  <button
                    data-testid={`delete-upload-${u.source_id}`} disabled={busy}
                    onClick={() => window.confirm(`Delete "${u.filename}" and all items extracted from it?`) && onDeleteUpload(u.source_id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-600 border border-rose-200 rounded-sm hover:bg-rose-50 transition-colors disabled:opacity-50"
                  >
                    <Trash size={15} /> Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5 h-fit">
        <h3 className="font-heading text-lg font-bold text-slate-900">Import options</h3>
        <div className="mt-4 space-y-2">
          {[["append", "Add to existing items", "Keeps current items and market costs"], ["replace", "Replace all items", "Clears existing items first"]].map(([v, l, d]) => (
            <label key={v} data-testid={`upload-mode-${v}`} className={`flex gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${mode === v ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:bg-slate-50"}`}>
              <input type="radio" name="mode" className="mt-1 accent-emerald-600" checked={mode === v} onChange={() => setMode(v)} />
              <span><span className="block text-sm font-medium text-slate-800">{l}</span><span className="block text-xs text-slate-500">{d}</span></span>
            </label>
          ))}
        </div>
        <ul className="mt-5 text-xs text-slate-500 space-y-1.5 list-disc pl-4">
          <li>Text-based PDFs and spreadsheets work best; scanned image PDFs are not supported yet.</li>
          <li>Always review extracted quantities and rates against the original tender.</li>
          <li>Items are auto-grouped into categories — you can change any category.</li>
        </ul>
      </div>
    </div>
  );
}
